import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { GET_MY_CHAT_UNREAD } from '../../graphql/queries/chat';
import { useViewer } from '../shell';
import { useBrand } from '../brand';
import { useTrack, useTrackExpand } from '../track';
import { useSiteTourStep } from '../tour';
import { PREF, useStickyPref } from '../lib/prefs';
import { useLiveRefresh } from '../lib/useLiveRefresh';
import { ChatPanel } from './ChatPanel';
import type { TippaRequest } from './TippaCards';
import './chat.css';

/** How often the closed chat checks for replies (also on page change and tab focus). */
export const UNREAD_POLL_MS = 120_000;

interface ChatState {
  open: boolean;
  unread: number;
  toggle(): void;
  /**
   * Opens the chat and asks for a multi or a quaddie straight away, as if the prompt
   * had been tapped. Only for members who may ask (subscribers): null for everyone else.
   */
  ask: ((request: TippaRequest) => void) | null;
  /** A bar that shows the chat button itself (the tracks drawer); returns its unregister. */
  registerBar(): () => void;
}

/** Null unless the viewer is a member with the chat turned on. */
const ChatContext = createContext<ChatState | null>(null);

/**
 * The chat with the team, for members with the customer_chat feature: one panel and
 * one unread count for the page. Pages with the tracks drawer show the chat button in
 * that bar (ChatBarButton); every other page gets the floating button. Nobody else
 * gets any of it, nor pays for a single request.
 */
export function ChatProvider({ children }: { children?: ReactNode }) {
  const viewer = useViewer();
  const enabled = viewer.isAuthenticated && viewer.chat === true;
  const location = useLocation();
  // ?chat=open (the link in the "you have a reply" email) opens the panel.
  const [open, setOpen] = useState(() => new URLSearchParams(location.search).get('chat') === 'open');
  // null until the first check answers, so a page load never mistakes "not asked yet" for "all read"
  const [unread, setUnread] = useState<number | null>(null);
  const [bars, setBars] = useState(0);
  /** A request from outside the panel (the home page banner), sent once the panel is open. */
  const [pendingAsk, setPendingAsk] = useState<TippaRequest | null>(null);
  const expand = useTrackExpand();

  const [, setNews] = useStickyPref(PREF.chatNews, 'shown', ['shown', 'dismissed'] as const);
  const toggle = useCallback(() => {
    expand('chat', !open);
    // opening the chat is the point of the "New" callout: it has done its job
    if (!open) setNews('dismissed');
    setOpen(!open);
  }, [expand, open, setNews]);

  // Multis and quaddies are for subscribers: everyone else's chat is for feedback and questions.
  const canAsk = viewer.isSubscribed;
  const ask = useCallback(
    (request: TippaRequest) => {
      if (!open) expand('chat', true);
      setNews('dismissed');
      // a copy per tap, so the same button tapped again (chat still open) asks again
      setPendingAsk({ ...request });
      setOpen(true);
    },
    [expand, open, setNews],
  );

  const registerBar = useCallback(() => {
    setBars((n) => n + 1);
    return () => setBars((n) => n - 1);
  }, []);

  const value = useMemo<ChatState | null>(
    () => (enabled ? { open, unread: open ? 0 : unread ?? 0, toggle, ask: canAsk ? ask : null, registerBar } : null),
    [enabled, open, unread, toggle, canAsk, ask, registerBar],
  );

  const brand = useBrand();
  const title = brand.chat?.title ?? 'Chat with us';
  const [alerting, dismissAlert] = useNewReplyAlert(enabled ? unread : null, open);
  useTitleCount(enabled && !open ? unread ?? 0 : 0);
  // On every page's help tour, for members with the chat (it's skipped where the button isn't on screen).
  useSiteTourStep(
    enabled
      ? {
          target: 'chat',
          title,
          body: canAsk
            ? "Message the team any time and we'll reply here. Or tap Build me a multi and pick a price for one built on the spot, or Give me a quaddie for one of today's."
            : "Questions, feedback or something not right? Message the team any time and we'll reply here.",
        }
      : null,
  );

  // The page itself never moves in or out of a wrapper here, so the flag arriving
  // after sign-in doesn't remount it.
  return (
    <ChatContext.Provider value={value}>
      {children}
      {value && (
        <>
          <UnreadPoller paused={open} onCount={setUnread} />
          <OpenLinkTidy />
          {open && (
            <ChatPanel
              onClose={() => {
                setPendingAsk(null);
                setOpen(false);
              }}
              initialAsk={pendingAsk}
              onInitialAsked={() => setPendingAsk(null)}
            />
          )}
          {!open && alerting && <NewReplyAlert unread={unread ?? 0} onOpen={toggle} onDismiss={dismissAlert} />}
          {!open && !alerting && canAsk && <ChatNews title={title} onTry={toggle} />}
          {bars === 0 && <FloatingChatButton />}
        </>
      )}
    </ChatContext.Provider>
  );
}

/** The chat state for this page, or null where the chat isn't available. */
export function useChat(): ChatState | null {
  return useContext(ChatContext);
}

/** Checks for unread replies while the panel is closed. */
function UnreadPoller({ paused, onCount }: { paused: boolean; onCount: (n: number) => void }) {
  const location = useLocation();
  const { data, refetch } = useQuery<{ myChatUnreadCount: number }>(GET_MY_CHAT_UNREAD, {
    skip: paused,
    fetchPolicy: 'cache-and-network',
  });
  useLiveRefresh(!paused, [refetch], UNREAD_POLL_MS);

  // A new page is a natural moment to check again.
  useEffect(() => {
    if (!paused) void refetch().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (data) onCount(data.myChatUnreadCount);
  }, [data, onCount]);
  return null;
}

/**
 * The one-off "New" callout above the chat button, telling members the chat can hand them a multi or a quaddie.
 * Shown until they dismiss it or open the chat (either way, for good on this device).
 */
function ChatNews({ title, onTry }: { title: string; onTry: () => void }) {
  const [news, setNews] = useStickyPref(PREF.chatNews, 'shown', ['shown', 'dismissed'] as const);
  const track = useTrack();
  useEffect(() => {
    if (news === 'shown') track('chat_news', { action: 'shown' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per page view while it shows
  }, [news]);
  if (news !== 'shown') return null;
  const close = (action: 'try' | 'dismiss') => {
    setNews('dismissed');
    track('chat_news', { action });
  };
  return (
    <div className="ui-chat-news" role="region" aria-label="New in the chat">
      <p>
        <b>New: ask for a multi or a quaddie.</b> Open {title}, pick a price, and we'll build you a multi on the latest
        prices in a couple of taps. Or get one of today's quaddies.
      </p>
      <div className="b">
        <button
          type="button"
          className="go"
          onClick={() => {
            close('try');
            onTry();
          }}
        >
          Try it
        </button>
        <button type="button" className="x" aria-label="Dismiss" onClick={() => close('dismiss')}>×</button>
      </div>
    </div>
  );
}

/** sessionStorage: the unread count this visit has already been alerted to, so it isn't repeated on every page. */
export const ALERTED_KEY = 'ui-chat-alerted';

const readAlerted = (): number => {
  try {
    return Number(window.sessionStorage.getItem(ALERTED_KEY)) || 0;
  } catch {
    return 0;
  }
};
const writeAlerted = (n: number) => {
  try {
    if (n > 0) window.sessionStorage.setItem(ALERTED_KEY, String(n));
    else window.sessionStorage.removeItem(ALERTED_KEY);
  } catch {
    // private browsing: the alert may repeat, which is harmless
  }
};

/**
 * Whether to show the "new message" alert: once each time the unread count goes up while the panel is closed (the
 * first check of a visit counts, so a reply that came in while the member was away is announced too). Reading the
 * replies (the count back to 0) starts afresh. `unread` is null until the first check, or where there's no chat.
 */
function useNewReplyAlert(unread: number | null, open: boolean): [boolean, () => void] {
  const [alerting, setAlerting] = useState(false);
  const track = useTrack();
  useEffect(() => {
    if (open) {
      setAlerting(false);
      return;
    }
    if (unread == null) return;
    if (unread === 0) {
      writeAlerted(0);
      setAlerting(false);
      return;
    }
    if (unread > readAlerted()) {
      writeAlerted(unread);
      setAlerting(true);
      track('chat_alert', { action: 'shown', unread });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- track is stable enough; only the count and panel matter
  }, [unread, open]);
  const dismiss = useCallback(() => {
    setAlerting(false);
    track('chat_alert', { action: 'dismiss' });
  }, [track]);
  return [alerting, dismiss];
}

const TITLE_COUNT = /^\(\d+\+?\) /;

/** "(2) " in front of the tab's title while replies are unread, kept there when a page changes the title. */
function useTitleCount(unread: number) {
  useEffect(() => {
    const apply = () => {
      const base = document.title.replace(TITLE_COUNT, '');
      const want = unread > 0 ? `(${unread > 9 ? '9+' : unread}) ${base}` : base;
      if (document.title !== want) document.title = want;
    };
    apply();
    const el = document.querySelector('title');
    const watch = el && typeof MutationObserver !== 'undefined' ? new MutationObserver(apply) : null;
    if (el) watch?.observe(el, { childList: true, characterData: true, subtree: true });
    return () => {
      watch?.disconnect();
      document.title = document.title.replace(TITLE_COUNT, '');
    };
  }, [unread]);
}

/** The pop-up above the chat button when a team reply arrives: open the chat from it, or dismiss it. */
function NewReplyAlert({ unread, onOpen, onDismiss }: { unread: number; onOpen: () => void; onDismiss: () => void }) {
  const track = useTrack();
  return (
    <div className="ui-chat-news ui-chat-alert" role="region" aria-label="New message">
      <p aria-live="polite">
        <b>{unread > 1 ? `You have ${unread > 9 ? '9+' : unread} new messages.` : 'You have a new message.'}</b> The team has replied in the chat.
      </p>
      <div className="b">
        <button
          type="button"
          className="go"
          onClick={() => {
            track('chat_alert', { action: 'open' });
            onOpen();
          }}
        >
          Open chat
        </button>
        <button type="button" className="x" aria-label="Dismiss" onClick={onDismiss}>×</button>
      </div>
    </div>
  );
}

/** Drops ?chat=open once it has opened the panel, so a refresh or a shared link doesn't reopen it. */
function OpenLinkTidy() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('chat') !== 'open') return;
    params.delete('chat');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  return null;
}

const label = (open: boolean, unread: number) =>
  open ? 'Close chat' : unread ? `Chat with us, ${unread} unread` : 'Chat with us';

function ChatIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      {open ? (
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      ) : (
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.6c-.5.4-1.3.1-1.3-.6V16A2.5 2.5 0 0 1 4 13.5z" fill="currentColor" />
      )}
    </svg>
  );
}

const Badge = ({ unread }: { unread: number }) =>
  unread > 0 ? <span className="ui-chat-badge">{unread > 9 ? '9+' : unread}</span> : null;

/** Bottom right, on pages without a bar to sit in. */
function FloatingChatButton() {
  const chat = useChat();
  if (!chat) return null;
  return (
    <button type="button" className="ui-chat-fab" data-tour="chat" aria-expanded={chat.open} aria-label={label(chat.open, chat.unread)} onClick={chat.toggle}>
      <ChatIcon open={chat.open} />
      <Badge unread={chat.unread} />
    </button>
  );
}

/**
 * The chat button inside a bar along the bottom of the page (the tracks drawer), so the
 * floating button doesn't cover the bar. While one is mounted the floating button hides.
 * Renders nothing where the chat isn't available.
 */
export function ChatBarButton() {
  const chat = useChat();
  const registerBar = chat?.registerBar;
  useEffect(() => registerBar?.(), [registerBar]);
  if (!chat) return null;
  return (
    <button type="button" className="ui-chat-barbtn" data-tour="chat" aria-expanded={chat.open} aria-label={label(chat.open, chat.unread)} onClick={chat.toggle}>
      <ChatIcon open={chat.open} />
      <Badge unread={chat.unread} />
    </button>
  );
}
